import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatProductPreview, ChatSalesContact, ChatService } from './chat-bot.service';

interface ChatMessage {
  role: string;
  content: string;
  products?: ChatProductPreview[];
  salesContacts?: ChatSalesContact[];
}

@Component({
  selector: 'app-chat-bot',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat-bot.html',
  styleUrl: './chat-bot.css',
})
export class ChatBot implements AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('messageTextarea') private messageTextarea?: ElementRef<HTMLTextAreaElement>;

  userInput = '';
  chatHistory: ChatMessage[] = [];
  isLoading = false;
  isOpen = false;
  private shouldScrollToBottom = false;
  private sessionId: string;

  constructor(private chatService: ChatService, private changeDetector: ChangeDetectorRef) {
    this.sessionId = this.chatService.getSessionId();
  }

  ngAfterViewChecked() {
    if (!this.shouldScrollToBottom) return;

    this.scrollToBottom();
    this.shouldScrollToBottom = false;
  }

  toggleChat() {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.queueScrollToBottom();
    }
  }

  closeChat() {
    this.isOpen = false;
  }

  resetChat() {
    this.chatHistory = [];
    this.userInput = '';
    this.sessionId = this.chatService.resetSession();
    this.resetComposerHeight();
    this.queueScrollToBottom();
  }

  send() {
    const textToSend = this.userInput.trim();

    if (!textToSend || this.isLoading) return;

    this.chatHistory.push({ role: 'User', content: textToSend });
    this.userInput = '';
    this.resetComposerHeight();
    this.isLoading = true;
    this.queueScrollToBottom();

    this.chatService.sendMessage(textToSend, this.sessionId).subscribe({
      next: (res) => {
        this.sessionId = res.sessionId;
        this.isLoading = false;
        this.chatHistory = [
          ...this.chatHistory,
          {
            role: 'Support',
            content: res.message,
            products: res.products ?? [],
            salesContacts: res.salesContacts ?? [],
          },
        ];
        this.flushChatView();
      },
      error: (err) => {
        console.error('Chatbot request failed:', err);
        this.isLoading = false;
        this.chatHistory = [
          ...this.chatHistory,
          { role: 'Sistem', content: 'Chatbot sedang tidak tersedia. Silakan coba kembali.' },
        ];
        this.flushChatView();
      },
    });
  }

  onMessageKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    this.send();
  }

  resizeComposer(event?: Event) {
    const textarea = (event?.target as HTMLTextAreaElement | null) ?? this.messageTextarea?.nativeElement;

    if (!textarea) return;

    const styles = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(styles.lineHeight) || 19;
    const paddingTop = Number.parseFloat(styles.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(styles.paddingBottom) || 0;
    const borderTop = Number.parseFloat(styles.borderTopWidth) || 0;
    const borderBottom = Number.parseFloat(styles.borderBottomWidth) || 0;
    const maxHeight = lineHeight * 3 + paddingTop + paddingBottom + borderTop + borderBottom;

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  private queueScrollToBottom() {
    this.shouldScrollToBottom = true;
  }

  private flushChatView() {
    this.queueScrollToBottom();
    this.changeDetector.detectChanges();
    window.setTimeout(() => this.scrollToBottom(), 0);
  }

  private resetComposerHeight() {
    window.setTimeout(() => {
      const textarea = this.messageTextarea?.nativeElement;

      if (!textarea) return;

      textarea.style.height = '';
      textarea.style.overflowY = 'hidden';
    }, 0);
  }

  private scrollToBottom() {
    const element = this.messagesContainer?.nativeElement;

    if (!element) return;

    element.scrollTop = element.scrollHeight;
  }
}
